/**
 * MokaQueueCard.jsx — `moka_payments` kuyruk kaydı (bkz. AdminMokaQueue.jsx).
 *
 * Aday listesi `match_status === 'queued'` (ve isimli/skorlu eşleşme —
 * services/moka/matching.py) için doludur; `unmatched_link_not_found`
 * (OtherTrxCode formatımıza uyuyor ama link kaydı yok — nadir/idari hata
 * durumu, bkz. services/moka/payment_processing.py) için `match_candidates`
 * HİÇ doldurulmuyor — bu kartta elle taksit ID girişiyle karşılanır
 * (görev talebinde bir arama widget'ı istenmedi, bkz. teslim raporu notu).
 *
 * Para kararı olduğu için onay TEK TIKLA yürümez: aday/elle taksit seçilir →
 * karşılaştırma paneli + POS hesabı seçilir → "Onayla ve Tahsil Et" bir
 * ConfirmDialog'a düşer (ikinci açık onay).
 */
import { useState } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, User, Calendar, Hash } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { getApiErrorMessage } from '@/utils/apiError';
import { fmtTRY } from '@/utils/formatCurrency';
import { ConfirmDialog, StatusBadge } from '@/components/admin';
import { useApproveMokaQueueItem, useRejectMokaQueueItem } from '@/api/mutations/moka';

function scoreVariant(score) {
  if (score >= 90) return 'success';
  if (score >= 70) return 'warning';
  return 'neutral';
}

function statusVariant(status) {
  if (status === 'approved' || status === 'auto_collected') return 'success';
  if (status === 'rejected' || status === 'ignored_not_successful') return 'danger';
  if (status === 'unmatched_link_not_found') return 'warning';
  return 'info'; // queued / pending
}

export default function MokaQueueCard({ item, posBanks, t, lang }) {
  const toast = useToast();
  const [selected, setSelected] = useState(null); // candidate obj | 'manual'
  const [manualInstallmentId, setManualInstallmentId] = useState('');
  const [bankId, setBankId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);

  const approve = useApproveMokaQueueItem();
  const reject = useRejectMokaQueueItem();

  const candidates = item.match_candidates || [];
  const reasonMessage = lang === 'tr' ? item.match_reason_message_tr : item.match_reason_message_en;
  const resolvedInstallmentId = selected === 'manual' ? manualInstallmentId.trim() : selected?.installment_id;
  const bankMissing = !bankId;
  const installmentMissing = !resolvedInstallmentId;
  const actionable = item.match_status === 'queued' || item.match_status === 'unmatched_link_not_found';

  const doApprove = async () => {
    try {
      await approve.mutateAsync({
        dealerPaymentId: item.dealer_payment_id,
        installmentId: resolvedInstallmentId,
        bankId,
      });
      toast.success(t('admin.moka_queue_approve_success'));
      setApproveConfirmOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      setApproveConfirmOpen(false);
    }
  };

  const doReject = async () => {
    try {
      await reject.mutateAsync({ dealerPaymentId: item.dealer_payment_id, note: rejectNote || undefined });
      toast.success(t('admin.moka_queue_reject_success'));
      setRejectConfirmOpen(false);
      setRejectOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      setRejectConfirmOpen(false);
    }
  };

  const openApproveConfirm = () => {
    setSubmitted(true);
    if (bankMissing || installmentMissing) return;
    setApproveConfirmOpen(true);
  };

  return (
    <div className="card-clay p-4" data-testid={`moka-queue-card-${item.dealer_payment_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-3 flex-wrap text-xs text-[var(--text-muted)]">
          <span className="font-bold text-base text-[var(--text-main)]">{fmtTRY(item.amount)}</span>
          <span className="flex items-center gap-1"><User size={12} /> {item.card_holder_name || '—'}</span>
          <span className="flex items-center gap-1"><Calendar size={12} /> {item.payment_date || '—'}</span>
          <span className="flex items-center gap-1 font-mono"><Hash size={11} /> {item.dealer_payment_id}</span>
        </div>
        <StatusBadge variant={statusVariant(item.match_status)} label={t(`admin.moka_queue_status_${item.match_status}`) || item.match_status} dot />
      </div>

      {reasonMessage && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 mb-3 text-xs text-amber-700 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {reasonMessage}
        </div>
      )}

      {!actionable ? null : (
        <>
          {/* ── Aday seçimi ─────────────────────────────────────────────────── */}
          <div className="mb-3">
            <p className="text-xs font-bold mb-1.5">{t('admin.moka_queue_candidates_title')}</p>
            {candidates.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {candidates.map((c) => (
                  <button
                    key={c.installment_id}
                    onClick={() => { setSelected(c); setSubmitted(false); }}
                    data-testid={`moka-candidate-${c.installment_id}`}
                    className={`text-left px-3 py-2 rounded-xl border-2 text-xs flex items-center justify-between gap-2 transition-colors ${
                      selected !== 'manual' && selected?.installment_id === c.installment_id
                        ? 'border-[var(--brand-primary)] bg-sky-50'
                        : 'border-[var(--border-light)] hover:border-[var(--brand-primary)]/50'
                    }`}
                  >
                    <span>
                      <strong>{c.user_name || '—'}</strong> — {fmtTRY(c.amount)} — {t('admin.acc_col_due')}: {c.due_date || '—'}
                      {c.installment_no ? ` — #${c.installment_no}` : ''}
                    </span>
                    <StatusBadge variant={scoreVariant(c.score)} label={`${c.score}`} />
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-1.5">
                <p className="text-[11px] text-[var(--text-muted)]">{t('admin.moka_queue_no_candidates')}</p>
                <input
                  type="text"
                  value={manualInstallmentId}
                  onChange={(e) => { setManualInstallmentId(e.target.value); setSelected('manual'); setSubmitted(false); }}
                  placeholder={t('admin.moka_queue_manual_installment_placeholder')}
                  data-testid="moka-manual-installment-input"
                  className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-mono text-xs"
                />
              </div>
            )}
          </div>

          {/* ── Karşılaştırma + onay ────────────────────────────────────────── */}
          {(selected === 'manual' ? manualInstallmentId.trim() : selected) && (
            <div className="border-t border-[var(--border-light)] pt-3 space-y-2" data-testid="moka-compare-panel">
              <p className="text-xs font-bold">{t('admin.moka_queue_compare_title')}</p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <span className="text-[var(--text-muted)]">{t('admin.moka_queue_compare_amount_payment')}</span>
                <span className="font-bold">{fmtTRY(item.amount)}</span>
                {selected !== 'manual' && (
                  <>
                    <span className="text-[var(--text-muted)]">{t('admin.moka_queue_compare_amount_installment')}</span>
                    <span className="font-bold">{fmtTRY(selected.amount)}</span>
                    <span className="text-[var(--text-muted)]">{t('admin.moka_queue_compare_name_payer')}</span>
                    <span>{item.card_holder_name || '—'}</span>
                    <span className="text-[var(--text-muted)]">{t('admin.moka_queue_compare_name_student')}</span>
                    <span>{selected.user_name || '—'}</span>
                    <span className="text-[var(--text-muted)]">{t('admin.moka_queue_compare_date')}</span>
                    <span>{item.payment_date || '—'}</span>
                    <span className="text-[var(--text-muted)]">{t('admin.moka_queue_compare_due')}</span>
                    <span>{selected.due_date || '—'}</span>
                  </>
                )}
              </div>
              <div>
                <label htmlFor={`moka-approve-bank-${item.dealer_payment_id}`} className="text-xs font-bold block mb-0.5">
                  {t('admin.moka_queue_approve_bank_label')} <span className="text-red-500">*</span>
                </label>
                <select
                  id={`moka-approve-bank-${item.dealer_payment_id}`}
                  value={bankId}
                  onChange={(e) => setBankId(e.target.value)}
                  data-testid="moka-approve-bank-select"
                  className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 font-semibold text-sm"
                >
                  <option value="">{t('admin.moka_bank_placeholder')}</option>
                  {posBanks.map((b) => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
                </select>
                {submitted && bankMissing && (
                  <p role="alert" className="text-[11px] font-bold text-rose-600 mt-0.5">{t('admin.moka_queue_select_bank_required')}</p>
                )}
                {submitted && installmentMissing && (
                  <p role="alert" className="text-[11px] font-bold text-rose-600 mt-0.5">{t('admin.moka_queue_select_installment_required')}</p>
                )}
              </div>
              <button
                onClick={openApproveConfirm}
                disabled={approve.isPending}
                data-testid="moka-approve-btn"
                className="btn-success px-4 py-2 text-xs font-bold disabled:opacity-50 min-h-[44px] inline-flex items-center gap-1.5"
              >
                <CheckCircle2 size={14} />
                {t('admin.moka_queue_approve_button')}
              </button>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-[var(--border-light)]">
            {!rejectOpen ? (
              <button
                onClick={() => setRejectOpen(true)}
                data-testid="moka-reject-open-btn"
                className="text-xs font-bold text-rose-600 hover:underline inline-flex items-center gap-1"
              >
                <XCircle size={13} /> {t('admin.moka_queue_reject_button')}
              </button>
            ) : (
              <div className="space-y-1.5">
                <textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder={t('admin.moka_queue_reject_note_label')}
                  rows={2}
                  data-testid="moka-reject-note-input"
                  className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-gray-50 text-xs"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => setRejectConfirmOpen(true)}
                    disabled={reject.isPending}
                    data-testid="moka-reject-confirm-open-btn"
                    className="px-3 py-1.5 rounded-xl bg-rose-50 text-rose-600 text-xs font-bold hover:bg-rose-100 disabled:opacity-50 min-h-[36px]"
                  >
                    {t('admin.moka_queue_reject_button')}
                  </button>
                  <button onClick={() => setRejectOpen(false)} className="px-3 py-1.5 text-xs text-[var(--text-muted)] min-h-[36px]">
                    {t('admin.moka_queue_cancel_selection')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={approveConfirmOpen}
        onOpenChange={(o) => { if (!o) setApproveConfirmOpen(false); }}
        title={t('admin.moka_queue_approve_confirm_title')}
        description={t('admin.moka_queue_approve_confirm_desc')}
        variant="warning"
        confirmLabel={t('admin.moka_queue_approve_button')}
        onConfirm={doApprove}
        loading={approve.isPending}
      />
      <ConfirmDialog
        open={rejectConfirmOpen}
        onOpenChange={(o) => { if (!o) setRejectConfirmOpen(false); }}
        title={t('admin.moka_queue_reject_confirm_title')}
        variant="danger"
        confirmLabel={t('admin.moka_queue_reject_button')}
        onConfirm={doReject}
        loading={reject.isPending}
      />
    </div>
  );
}
